import { useEffect, useMemo, useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import { Input, Textarea } from "@/components/base/Field";
import { LoadingState, ErrorState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  cancelPayroll,
  getPayrollPreview,
  getPayrollRuns,
  monthLabel,
  validatePayroll,
  type PayrollLine,
  type PayrollRun,
} from "@/services/payrollService";
import { formatDate, formatMoney, todayISO, plural } from "@/utils/format";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import type { Cycle } from "@/types";
import NumberInput from "@/components/base/NumberInput";

interface Props {
  cycles: Cycle[];
  /** Après validation ou annulation (les dépenses « Salaires » ont changé). */
  onChanged: () => void;
}

const currentMonth = () => todayISO().slice(0, 7);

/**
 * Paie du mois : personnel au salaire fixe (modifiable pour le mois), enseignants heures × tarif (heures
 * pré-remplies : hebdomadaires × 4). Chacun n'est payé qu'une fois par mois : « Payer » sur une ligne paie
 * une seule personne, « Valider la paie » toutes celles qui restent. Un paiement écrit une dépense
 * « Salaires » par cycle ; il se consulte et peut être annulé avec un motif.
 */
export default function PayrollTab({ cycles, onChanged }: Props) {
  const { notify } = useToast();
  const { can } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [paidOn, setPaidOn] = useState(todayISO());
  const [lines, setLines] = useState<PayrollLine[] | null>(null);
  const linesPager = usePager();
  const runsPager = usePager();
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<PayrollRun | null>(null);
  const [toCancel, setToCancel] = useState<PayrollRun | null>(null);
  const [reason, setReason] = useState("");
  /** Personnes à payer, en attente de confirmation. */
  const [toPay, setToPay] = useState<PayrollLine[] | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [preview, history] = await Promise.all([getPayrollPreview(month), getPayrollRuns()]);
      setLines(preview.lines);
      setRuns(history);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Paie indisponible.");
    }
  };

  useEffect(() => {
    setLines(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const cycleLabel = (id: string) => cycles.find((c) => c.id === id)?.label ?? "—";
  const isPaid = (l: PayrollLine) => !!l.paidInRunId;
  const unpaid = useMemo(() => (lines ?? []).filter((l) => !isPaid(l)), [lines]);
  const total = useMemo(() => unpaid.reduce((s, l) => s + l.amount, 0), [unpaid]);
  const paidTotal = useMemo(
    () => (lines ?? []).filter(isPaid).reduce((s, l) => s + l.amount, 0),
    [lines],
  );
  const byCycle = useMemo(() => {
    const map = new Map<string, number>();
    unpaid.forEach((l) => map.set(l.cycleId, (map.get(l.cycleId) ?? 0) + l.amount));
    return [...map.entries()];
  }, [unpaid]);

  // Lignes repérées par la personne (et non par leur rang : la liste est paginée).
  const samePerson = (a: PayrollLine, b: PayrollLine) => a.personType === b.personType && a.personId === b.personId;
  const setHours = (line: PayrollLine, value: string) =>
    setLines((prev) =>
      (prev ?? []).map((l) =>
        samePerson(l, line) ? { ...l, hours: Number(value) || 0, amount: Math.round((Number(value) || 0) * (l.hourlyRate ?? 0)) } : l,
      ),
    );
  const setAmount = (line: PayrollLine, value: string) =>
    setLines((prev) => (prev ?? []).map((l) => (samePerson(l, line) ? { ...l, amount: Number(value) || 0 } : l)));

  /** Demande confirmation avant de payer les personnes données (une seule, ou toutes celles qui restent). */
  const askPay = (toPay: PayrollLine[]) => {
    const payable = toPay.filter((l) => l.amount > 0);
    if (!payable.length) {
      notify("Aucun montant à verser.", "error");
      return;
    }
    setToPay(payable);
  };

  const confirmPay = async () => {
    if (!toPay) return;
    setBusy(true);
    try {
      await validatePayroll(month, paidOn, toPay);
      setToPay(null);
      notify(
        toPay.length === 1
          ? `${toPay[0].personName} payé(e) pour ${monthLabel(month)} : dépense « Salaires » enregistrée.`
          : `Paie de ${monthLabel(month)} validée : dépenses « Salaires » enregistrées.`,
      );
      await load();
      onChanged();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Validation impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (!toCancel) return;
    if (!reason.trim()) {
      notify("Indiquez le motif de l'annulation.", "error");
      return;
    }
    setBusy(true);
    try {
      await cancelPayroll(toCancel.id, reason);
      notify(`Paiement de ${monthLabel(toCancel.month)} annulé : ses dépenses sont retirées.`, "info");
      setToCancel(null);
      setReason("");
      await load();
      onChanged();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Annulation impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (!can("payroll:read")) {
    return (
      <Card>
        <p className="py-6 text-center text-sm text-foreground-500">
          La paie concerne toute l'école : elle est réservée aux rôles globaux (Direction, Administrateur).
        </p>
      </Card>
    );
  }
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!lines) return <LoadingState label="Préparation de la paie..." />;

  const canPay = can("payroll:create");
  const linesPage = linesPager.slice(lines, month);
  const runsPage = runsPager.slice(runs);

  return (
    <div className="space-y-5">
      <Card>
        <SectionTitle
          icon="ri-calendar-check-line"
          title={`Paie de ${monthLabel(month)}`}
          subtitle="Personnel : salaire fixe • Enseignants : heures du mois × tarif horaire"
        />
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Input label="Mois" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          <Input label="Date du paiement" type="date" value={paidOn} max={todayISO()} onChange={(e) => setPaidOn(e.target.value)} />
          {paidTotal > 0 && (
            <Badge tone="success" icon="ri-checkbox-circle-line">
              {unpaid.length === 0 ? "Tout le monde est payé pour ce mois" : `Déjà versé ce mois : ${formatMoney(paidTotal)}`}
            </Badge>
          )}
        </div>

        <div className="overflow-x-auto rounded-md border border-background-200">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-background-100">
              <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-3.5 py-2 font-medium">Personne</th>
                <th className="px-3.5 py-2 font-medium">Cycle</th>
                <th className="px-3.5 py-2 text-right font-medium">Heures</th>
                <th className="px-3.5 py-2 text-right font-medium">Tarif horaire</th>
                <th className="px-3.5 py-2 text-right font-medium">Montant</th>
                <th className="px-3.5 py-2 text-right font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {linesPage.items.map((l) => (
                <tr
                  key={`${l.personType}-${l.personId}`}
                  className={`border-t border-background-200 ${isPaid(l) ? "bg-background-100/60" : ""}`}
                >
                  <td className="px-3.5 py-2">
                    <p className="text-foreground-900">{l.personName}</p>
                    <p className="text-xs text-foreground-500">{l.personType === "ENSEIGNANT" ? "Enseignant" : "Personnel"}</p>
                  </td>
                  <td className="px-3.5 py-2 text-foreground-700">{cycleLabel(l.cycleId)}</td>
                  <td className="px-3.5 py-2 text-right">
                    {l.personType === "ENSEIGNANT" && l.hours !== null ? (
                      <NumberInput
                        min={0}
                        step="0.5"
                        value={l.hours ?? 0}
                        disabled={isPaid(l) || !canPay}
                        onChange={(e) => setHours(l, e.target.value)}
                        className="w-20 rounded-md border border-background-300 px-2 py-1 text-right text-sm tabular-nums disabled:bg-background-100"
                      />
                    ) : (
                      <span className="text-foreground-400">—</span>
                    )}
                  </td>
                  <td className="px-3.5 py-2 text-right tabular-nums text-foreground-600">
                    {l.hourlyRate ? formatMoney(l.hourlyRate) : l.personType === "ENSEIGNANT" ? "Fixe" : "—"}
                  </td>
                  <td className="px-3.5 py-2 text-right">
                    {l.hours === null ? (
                      <NumberInput
                        min={0}
                        value={l.amount}
                        disabled={isPaid(l) || !canPay}
                        onChange={(e) => setAmount(l, e.target.value)}
                        className="w-32 rounded-md border border-background-300 px-2 py-1 text-right text-sm tabular-nums disabled:bg-background-100"
                      />
                    ) : (
                      <span className="tabular-nums font-medium text-foreground-900">{formatMoney(l.amount)}</span>
                    )}
                  </td>
                  <td className="px-3.5 py-2 text-right">
                    {isPaid(l) ? (
                      <Badge tone="success" icon="ri-check-line">
                        Payé
                      </Badge>
                    ) : canPay ? (
                      <Button
                        size="sm"
                        variant="outline"
                        icon="ri-hand-coin-line"
                        onClick={() => askPay([l])}
                        disabled={busy || l.amount <= 0}
                      >
                        Payer
                      </Button>
                    ) : (
                      <span className="text-xs text-foreground-400">À payer</span>
                    )}
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3.5 py-6 text-center text-xs text-foreground-400">
                    Aucune personne à payer : définissez les salaires du personnel et le tarif horaire des enseignants.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination pager={linesPage} className="!px-0" />

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2 text-xs">
            {byCycle.map(([cycleId, amount]) => (
              <Badge key={cycleId} tone="secondary">
                {cycleLabel(cycleId)} : {formatMoney(amount)}
              </Badge>
            ))}
            <Badge tone="primary">Reste à payer : {formatMoney(total)}</Badge>
          </div>
          {canPay && unpaid.length > 0 && (
            <Button icon="ri-check-double-line" onClick={() => askPay(unpaid)} disabled={busy || total <= 0}>
              {busy ? "Validation..." : "Valider la paie"}
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <SectionTitle icon="ri-history-line" title="Paiements enregistrés" subtitle="Chaque personne n'est payée qu'une fois par mois" />
        {runs.length === 0 ? (
          <p className="py-4 text-center text-xs text-foreground-400">Aucun paiement enregistré.</p>
        ) : (
          <>
          <div className="divide-y divide-background-200 rounded-md border border-background-200">
            {runsPage.items.map((run) => (
              <div key={run.id} className={`flex flex-wrap items-center gap-3 px-4 py-2.5 ${run.cancelledAt ? "opacity-60" : ""}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900">
                    {monthLabel(run.month)}
                    {run.lines.length === 1 && <span className="font-normal text-foreground-600"> — {run.lines[0].personName}</span>}
                    {run.cancelledAt && <span className="ml-2"><Badge tone="accent">Annulé</Badge></span>}
                  </p>
                  <p className="text-xs text-foreground-500">
                    Payée le {formatDate(run.paidOn)} par {run.recordedBy} • {plural(run.lines.length, "personne")}
                    {run.cancelledAt ? ` • annulée par ${run.cancelledBy} : ${run.cancelReason}` : ""}
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-foreground-900">{formatMoney(run.total)}</span>
                <Button size="sm" variant="outline" icon="ri-eye-line" onClick={() => setDetail(run)}>
                  Détail
                </Button>
                {can("payroll:delete") && !run.cancelledAt && (
                  <Button size="sm" variant="ghost" icon="ri-close-circle-line" onClick={() => { setReason(""); setToCancel(run); }}>
                    Annuler
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Pagination pager={runsPage} className="!px-0" />
          </>
        )}
      </Card>

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? `Paie de ${monthLabel(detail.month)}` : ""}
        subtitle={detail ? `Payée le ${formatDate(detail.paidOn)} — ${formatMoney(detail.total)}` : undefined}
        icon="ri-wallet-3-line"
        size="lg"
      >
        <table className="w-full text-sm">
          <tbody>
            {detail?.lines.map((l) => (
              <tr key={`${l.personType}-${l.personId}`} className="border-t border-background-200">
                <td className="py-2 text-foreground-900">{l.personName}</td>
                <td className="py-2 text-foreground-600">{cycleLabel(l.cycleId)}</td>
                <td className="py-2 text-right text-foreground-600">
                  {l.hours !== null ? `${l.hours} h × ${formatMoney(l.hourlyRate ?? 0)}` : "Salaire"}
                </td>
                <td className="py-2 text-right font-medium tabular-nums">{formatMoney(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>

      <Modal
        open={toCancel !== null}
        onClose={() => setToCancel(null)}
        title="Annuler le paiement"
        subtitle={toCancel ? `${monthLabel(toCancel.month)} — ${formatMoney(toCancel.total)}` : undefined}
        icon="ri-close-circle-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setToCancel(null)}>
              Retour
            </Button>
            <Button variant="danger" onClick={confirmCancel} disabled={busy}>
              Annuler le paiement
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-foreground-600">
          Les dépenses « Salaires » de ce paiement sont retirées ; les personnes concernées pourront être payées à nouveau pour ce mois.
        </p>
        <Textarea label="Motif" required rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>

      <Modal
        open={toPay !== null}
        onClose={() => !busy && setToPay(null)}
        title={toPay?.length === 1 ? `Payer ${toPay[0].personName}` : "Valider la paie"}
        subtitle={toPay ? `${monthLabel(month)} — payé le ${formatDate(paidOn)}` : undefined}
        icon="ri-hand-coin-line"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setToPay(null)} disabled={busy}>
              Retour
            </Button>
            <Button icon="ri-check-double-line" onClick={confirmPay} disabled={busy}>
              {busy ? "Paiement..." : "Confirmer le paiement"}
            </Button>
          </>
        }
      >
        {toPay && (
          <div className="space-y-3">
            <p className="text-sm text-foreground-600">
              {toPay.length === 1
                ? "Cette personne sera marquée payée pour ce mois et ne pourra plus être payée à nouveau (sauf annulation)."
                : `${toPay.length} personnes seront marquées payées pour ce mois et ne pourront plus être payées à nouveau (sauf annulation).`}
            </p>
            <div className="max-h-64 overflow-y-auto rounded-md border border-background-200">
              <table className="w-full text-sm">
                <tbody>
                  {toPay.map((l) => (
                    <tr key={`${l.personType}-${l.personId}`} className="border-t border-background-200 first:border-0">
                      <td className="px-3 py-1.5 text-foreground-900">{l.personName}</td>
                      <td className="px-3 py-1.5 text-foreground-600">{cycleLabel(l.cycleId)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {[...toPay.reduce((m, l) => m.set(l.cycleId, (m.get(l.cycleId) ?? 0) + l.amount), new Map<string, number>())].map(
                ([cycleId, amount]) => (
                  <Badge key={cycleId} tone="secondary">
                    {cycleLabel(cycleId)} : {formatMoney(amount)}
                  </Badge>
                ),
              )}
              <Badge tone="primary">Total : {formatMoney(toPay.reduce((s, l) => s + l.amount, 0))}</Badge>
            </div>
            <p className="text-xs text-foreground-500">Les dépenses « Salaires » correspondantes seront créées automatiquement.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
